<template>
  <StoreLocatorConfigSection
    :title="$t('message.storeLocator.facilities.title')"
  >
    <div class="sl-facilities">
      <div class="sl-facilities__label">
        {{ $t("message.storeLocator.facilities.show") }}:
      </div>
      <div class="sl-facilities__radios">
        <v-radio-group v-model="type" color="primary" row>
          <v-radio
            :label="
              $t('message.storeLocator.facilities.allFacilities', [
                facilitiesCount
              ])
            "
            :value="TYPES.ALL_FACILITIES"
          ></v-radio>
          <v-radio
            :label="$t('message.storeLocator.facilities.selectedGroups')"
            :value="TYPES.SELECTED_GROUPS"
          ></v-radio>
        </v-radio-group>
      </div>

      <div v-if="type === TYPES.SELECTED_GROUPS" class="sl-facilities__list">
        <SectionLoader :status="groupsLoading" :opacity="1" />
        <div
          v-for="group in allGroups"
          :key="group.id"
          class="sl-facilities__list-item"
        >
          <v-checkbox v-model="selectedGroupsState[group.id]" hide-details>
            <template #label>
              <div
                class="d-flex align-items-center justify-space-between flex-grow-1"
              >
                <div class="sl-facilities__list-item--label">
                  {{ group.name }}
                </div>
                <div class="sl-facilities__list-item--counter">
                  {{ getFacilitiesCountLabel(group.facilities_count) }}
                </div>
              </div>
            </template>
          </v-checkbox>
        </div>
      </div>
    </div>
  </StoreLocatorConfigSection>
</template>

<script>
import StoreLocatorConfigSection from "@/components/StoreLocator/StoreLocatorConfigSection";
import serviceHandler from "@/helpers/handlers/service";
import facilitiesGroups from "@/services/facilitiesGroups";
import { pluralCount } from "@/helpers/helpers";
import SectionLoader from "@/components/Loader/SectionLoader";

const TYPES = {
  ALL_FACILITIES: 1,
  SELECTED_GROUPS: 2
};

export default {
  name: "StoreLocatorConfigFacilities",
  components: {
    StoreLocatorConfigSection,
    SectionLoader
  },
  model: {
    prop: "value",
    event: "change"
  },
  props: {
    value: {
      type: [String, Array],
      validator: val => {
        if (typeof val === "string") return val === "all";
        return true;
      }
    },
    facilitiesCount: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    TYPES,
    type: TYPES.ALL_FACILITIES,
    allGroups: null,
    selectedGroupsState: null,
    groupsLoading: false
  }),
  computed: {
    selectedGroupsIds() {
      return this.selectedGroupsState
        ? Object.keys(this.selectedGroupsState).reduce(
            (ids, i) => (this.selectedGroupsState[i] ? [...ids, i] : ids),
            []
          )
        : [];
    }
  },
  watch: {
    async type(value) {
      if (value === TYPES.SELECTED_GROUPS) {
        if (!this.allGroups) {
          await this.fetchGroups();
        }
      }

      this.onChange();
    },
    selectedGroupsIds() {
      this.onChange();
    }
  },
  async mounted() {
    this.type =
      this.value === "all" ? TYPES.ALL_FACILITIES : TYPES.SELECTED_GROUPS;
  },
  methods: {
    async fetchGroups() {
      this.groupsLoading = true;
      this.allGroups = (
        await serviceHandler(facilitiesGroups.getAllFacilitiesGroups())
      ).results;
      this.setGroupsState();
      this.groupsLoading = false;
    },

    setGroupsState() {
      this.selectedGroupsState = this.allGroups.reduce(
        (obj, i) => ({
          ...obj,
          [i.id]:
            Array.isArray(this.value) && this.value.some(id => id === i.id)
        }),
        {}
      );
    },

    onChange() {
      const data =
        this.type === TYPES.ALL_FACILITIES ? "all" : this.selectedGroupsIds;
      this.$emit("change", data);
    },

    getFacilitiesCountLabel(count) {
      return pluralCount("message.pluralForms.facility", count);
    }
  }
};
</script>

<style lang="scss" scoped>
.sl-facilities {
  margin-bottom: $spacing * 2.5;

  &__label {
    @include font-size(12px);
    line-height: 17px;
    font-weight: normal;
    color: rgba($dark-blue-3, 0.6);
  }

  &__radios {
    ::v-deep .v-label {
      @include font-size(14px);
      line-height: 17px;
      color: rgba($dark-blue-3, 0.9);
    }
  }

  &__list {
    height: 253px;
    overflow-y: auto;
    border-radius: 15px;
    border: 1px solid rgba($dark-blue-3, 0.06);
    position: relative;
  }

  &__list-item {
    &--label {
      @include font-size(14px);
      line-height: 17px;
      color: $dark-blue-3;
      font-weight: 500;
    }

    &--counter {
      @include font-size(12px);
      line-height: 17px;
      color: rgba($dark-blue-3, 0.65);
    }

    .v-input--checkbox {
      margin-top: 0;
      padding-top: 0;

      ::v-deep .v-input__slot {
        padding: $spacing * 2.5;
      }
    }

    &:not(:last-of-type) {
      border-bottom: 1px solid rgba($dark-blue-3, 0.06);
    }
  }
}
</style>
